import { Center, Text3D } from '@react-three/drei'
import { useFrame } from '@react-three/fiber'
import { useAtomValue } from 'jotai'
import { trackEvent } from '../api'
import { useEffect, useRef, useState } from 'react'
import * as THREE from 'three'
import { useParams } from 'wouter'
import { clientAtom, languageAtom, minigameDodgeFrameAtom, minigameStateAtom, teamsAtom } from '../GlobalState'
import Rocket from '../meshes/Rocket'
import Star from '../meshes/Stars/Star'
import UfoNew from '../meshes/UfoNew'
import YutBonus from '../components/YutBonus'
import useStarRoll from '../shader/starRoll/StarRoll'
import { getSocket } from '../socket'
import { getNameFont } from '../logicHelpers/helpers'
import translations from '../dictionaries/translations'

const socket = getSocket()
const font = '/fonts/Luckiest Guy_Regular.json'
const ARENA_WIDTH = 8
const ARENA_HEIGHT = 20
const ARENA_Z_OFFSET = -3
const CELL_HEIGHT = 1.35
const CELL_TWEEN_DURATION = 0.125

function Label({ children, color = 'white', position, size = 0.4 }) {
  return <Text3D font={getNameFont(String(children), font)} size={size} height={0.01} position={position} rotation={[-Math.PI / 2, 0, 0]}>
    <meshStandardMaterial color={color} />
    {children}
  </Text3D>
}

function ControlButton({ direction, position, onClick }) {
  const [hovered, setHovered] = useState(false)
  const rotations = {
    up: [-Math.PI / 2, 0, 0],
    down: [Math.PI / 2, 0, 0],
    left: [0, 0, Math.PI / 2],
    right: [0, 0, -Math.PI / 2]
  }
  return <group position={position}>
    <mesh><boxGeometry args={[1.8, 0.05, 1.8]} /><meshStandardMaterial color={hovered ? 'limegreen' : 'yellow'} /></mesh>
    <mesh
      position={[0, 0.04, 0]}
      onPointerEnter={(event) => { event.stopPropagation(); setHovered(true); document.body.style.cursor = 'pointer' }}
      onPointerLeave={(event) => { event.stopPropagation(); setHovered(false); document.body.style.cursor = 'default' }}
      onPointerDown={(event) => { event.stopPropagation(); onClick() }}
    ><boxGeometry args={[1.8, 0.1, 1.8]} /><meshStandardMaterial transparent opacity={0} /></mesh>
    <mesh position={[0, 0.16, 0]} rotation={rotations[direction]}>
      <coneGeometry args={[0.32, 0.84, 3]} />
      <meshStandardMaterial color='black' />
    </mesh>
  </group>
}

function ActionButton({ children, position, onClick }) {
  const [hovered, setHovered] = useState(false)
  return <group position={position}>
    <mesh><boxGeometry args={[4.1, 0.05, 1.2]} /><meshStandardMaterial color={hovered ? 'limegreen' : 'yellow'} /></mesh>
    <mesh
      position={[0, 0.04, 0]}
      onPointerEnter={() => { setHovered(true); document.body.style.cursor = 'pointer' }}
      onPointerLeave={() => { setHovered(false); document.body.style.cursor = 'default' }}
      onPointerDown={(event) => { event.stopPropagation(); onClick() }}
    ><boxGeometry args={[4.1, 0.1, 1.2]} /><meshStandardMaterial transparent opacity={0} /></mesh>
    <Center position={[0, 0.06, 0]}><Text3D font={getNameFont(String(children), font)} size={0.5} height={0.01} rotation={[-Math.PI / 2, 0, 0]}><meshStandardMaterial color='black' />{children}</Text3D></Center>
  </group>
}

function gridPosition(item, board) {
  const width = board?.width ?? 10
  const columnSubdivisions = board?.columnSubdivisions ?? 1
  const rowSubdivisions = board?.rowSubdivisions ?? 1
  return [
    -ARENA_WIDTH / 2 + ((item.x / columnSubdivisions) / Math.max(1, width / columnSubdivisions - 1)) * ARENA_WIDTH,
    (item.z / rowSubdivisions) * CELL_HEIGHT + ARENA_Z_OFFSET
  ]
}

function SideWarning({ position }) {
  const lightRef = useRef(null)

  useFrame(({ clock }) => {
    if (lightRef.current) lightRef.current.intensity = 1.2 + Math.abs(Math.sin(clock.elapsedTime * 12)) * 2.4
  })

  return <group position={position}>
    <pointLight ref={lightRef} color='#ff3a1e' distance={3} />
    <Star scale={0.42} color='#ff3a1e' />
  </group>
}

function StarfallBorder({ board, side }) {
  const stars = useRef([])
  const startZ = ARENA_Z_OFFSET - 15
  const endZ = ((board?.maxZ ?? 10) / (board?.rowSubdivisions ?? 1)) * CELL_HEIGHT + ARENA_Z_OFFSET + 0.7
  const streamLength = endZ - startZ
  const columnSubdivisions = board?.columnSubdivisions ?? 1
  const gridEdgeColumn = side < 0 ? 0 : (board?.width ?? 1) - columnSubdivisions
  const spawnLaneColumn = side < 0 ? -columnSubdivisions : (board?.width ?? 1)
  const [gridEdgeX] = gridPosition({ x: gridEdgeColumn, z: 0 }, board)
  const [spawnLaneX] = gridPosition({ x: spawnLaneColumn, z: 0 }, board)
  const dividerX = (gridEdgeX + spawnLaneX) / 2

  useFrame((_, delta) => {
    for (const star of stars.current) {
      if (!star) continue
      star.position.z += delta * 2.5
      star.rotation.y += delta * 0.7
      if (star.position.z > endZ) star.position.z -= streamLength
    }
  })

  return <group>
    {Array.from({ length: 20 }, (_value, index) => {
      const progress = index / 20
      return <group
        key={index}
        ref={(element) => { stars.current[index] = element }}
        position={[dividerX, 0.12, startZ + progress * streamLength]}
      >
        <Star scale={0.16 + (index % 3) * 0.035} color={'#58e8ff'} opacity={0.7} />
      </group>
    })}
  </group>
}

function FallingStar({ projectile, board, stepInterval, onTrailBurst }) {
  const starRef = useRef(null)
  const tween = useRef(null)
  const pendingCells = useRef([])
  const trailBurstElapsed = useRef(0)
  const previousCell = useRef({ x: projectile.x, z: projectile.type === 'mars' ? projectile.z : projectile.z - 1 })
  const initialPosition = useRef(null)
  const warningStartZ = (board?.maxZ ?? 10) - 3 * (board?.rowSubdivisions ?? 1)
  const hasWarning = useRef(projectile.z >= warningStartZ)
  const [x, z] = gridPosition(projectile, board)
  const isMars = projectile.type === 'mars'
  const isGreen = projectile.type === 'green'
  const isHorizontal = projectile.type === 'horizontal'
  const isWaiting = isHorizontal && projectile.waitUntil > Date.now()
  const warningActive = isHorizontal && projectile.warningUntil > Date.now()
  if (projectile.z >= warningStartZ) hasWarning.current = true

  if (!initialPosition.current) initialPosition.current = [x, 0.16, isMars || isHorizontal ? z : z - CELL_HEIGHT]

  useEffect(() => {
    if (!starRef.current) return
    const start = previousCell.current
    let cell = { ...start }

    while (cell.x !== projectile.x || cell.z !== projectile.z) {
      cell = {
        x: cell.x === projectile.x ? cell.x : cell.x + Math.sign(projectile.x - cell.x),
        z: cell.x === projectile.x && cell.z !== projectile.z ? cell.z + Math.sign(projectile.z - cell.z) : cell.z
      }
      pendingCells.current.push(cell)
    }
    previousCell.current = { x: projectile.x, z: projectile.z }
  }, [projectile.x, projectile.z])

  useFrame((_, delta) => {
    if (!starRef.current) return
    const waitingOnSide = isHorizontal && projectile.waitUntil > Date.now()
    starRef.current.rotation.y += delta * (waitingOnSide ? 2 : 8)
    starRef.current.rotation.x += delta * (waitingOnSide ? 1.25 : 5)
    if (waitingOnSide) {
      const columnSubdivisions = board?.columnSubdivisions ?? 1
      const outwardColumn = projectile.x - projectile.direction * columnSubdivisions * 1.5
      const [outwardX] = gridPosition({ x: outwardColumn, z: projectile.z }, board)
      const waitDuration = Math.max(1, projectile.waitUntil - (projectile.waitStartedAt ?? projectile.waitUntil))
      const waitProgress = THREE.MathUtils.clamp((Date.now() - (projectile.waitStartedAt ?? projectile.waitUntil)) / waitDuration, 0, 1)
      const sway = Math.pow(Math.sin(waitProgress * Math.PI * 2), 2)
      starRef.current.position.x = THREE.MathUtils.lerp(x, outwardX, sway)
      starRef.current.position.z = z
      starRef.current.scale.x = 1
      starRef.current.scale.z = 1
      return
    }
    starRef.current.scale.x = 1
    starRef.current.scale.z = 1
    let remainingDelta = delta

    while (remainingDelta > 0) {
      if (!tween.current && pendingCells.current.length > 0) {
        const nextCell = pendingCells.current.shift()
        const [nextX, nextZ] = gridPosition(nextCell, board)
        tween.current = {
          fromX: starRef.current.position.x,
          fromZ: starRef.current.position.z,
          toX: nextX,
          toZ: nextZ,
          elapsed: 0
        }
      }
      if (!tween.current) return

      const tweenDuration = Math.max(
        0.04,
        stepInterval ? stepInterval / 1000 : CELL_TWEEN_DURATION
      )
      const remainingTweenTime = tweenDuration - tween.current.elapsed
      const step = Math.min(remainingDelta, remainingTweenTime)
      tween.current.elapsed += step
      remainingDelta -= step
      const progress = tween.current.elapsed / tweenDuration
      starRef.current.position.x = THREE.MathUtils.lerp(tween.current.fromX, tween.current.toX, progress)
      starRef.current.position.z = THREE.MathUtils.lerp(tween.current.fromZ, tween.current.toZ, progress)
      trailBurstElapsed.current += step
      if (!isMars && onTrailBurst && trailBurstElapsed.current >= 0.3) {
        trailBurstElapsed.current = 0
        onTrailBurst({
          position: starRef.current.position.clone(),
          rotation: new THREE.Vector3(0, Math.random() * Math.PI, 0),
          omitFactor: 15,
          size: 0.001,
          scale: 0.32,
          color: new THREE.Color(starColor),
          duration: 0.45,
          speedX: (Math.random() - 0.5) * 4.5,
          speedY: -1.8,
          brightness: 0.2
        })
      }

      if (progress === 1) tween.current = null
    }
  })

  const starColor = isMars ? '#d85b34' : isGreen ? '#63e66b' : isWaiting ? Math.floor(Date.now() / 250) % 2 === 0 ? 'yellow' : '#ff3a1e' : hasWarning.current ? '#ff5a36' : 'yellow'
  const warningX = projectile.warningSide === 'left' ? -ARENA_WIDTH / 2 : ARENA_WIDTH / 2

  return <group ref={starRef} position={initialPosition.current}>
    <Star scale={isMars ? 0.92 : 0.72} color={starColor} />
    {(hasWarning.current || warningActive) && <pointLight color='#ff3a1e' intensity={1.2} distance={2.5} />}
    {warningActive && <SideWarning position={[warningX - x, 0, 0]} />}
  </group>
}

function GreenStarTransfer({ transfer, board, onComplete }) {
  const transferRef = useRef(null)
  const startedAt = useRef(null)
  const completed = useRef(false)
  const [fromX, fromZ] = gridPosition(transfer.from, board)
  const [toX, toZ] = gridPosition(transfer.to, board)

  useFrame(({ clock }, delta) => {
    if (!transferRef.current) return
    if (startedAt.current === null) startedAt.current = clock.elapsedTime
    const progress = Math.min(1, (clock.elapsedTime - startedAt.current) / (transfer.duration / 1000))
    transferRef.current.position.x = THREE.MathUtils.lerp(fromX, toX, progress)
    transferRef.current.position.z = THREE.MathUtils.lerp(fromZ, toZ, progress)
    transferRef.current.rotation.y += delta * 8
    if (progress === 1 && !completed.current) {
      completed.current = true
      onComplete(transfer.id)
    }
  })

  return <group ref={transferRef} position={[fromX, 0.22, fromZ]}>
    <Star scale={0.34} color='#63e66b' opacity={0.4} />
  </group>
}

function DodgePlayer({ player, board, isCurrentPlayer }) {
  const playerRef = useRef(null)
  const tween = useRef(null)
  const initialPosition = useRef(null)
  const [targetX, targetZ] = gridPosition(player, board)

  if (!initialPosition.current) initialPosition.current = [targetX, 0.22, targetZ]

  useEffect(() => {
    if (!playerRef.current) return
    tween.current = {
      fromX: playerRef.current.position.x,
      fromZ: playerRef.current.position.z,
      toX: targetX,
      toZ: targetZ,
      elapsed: 0
    }
  }, [targetX, targetZ])

  useFrame((_, delta) => {
    if (!playerRef.current || !tween.current) return
    tween.current.elapsed = Math.min(CELL_TWEEN_DURATION, tween.current.elapsed + delta)
    const progress = tween.current.elapsed / CELL_TWEEN_DURATION
    const easedProgress = 1 - Math.pow(1 - progress, 3)
    playerRef.current.position.x = THREE.MathUtils.lerp(tween.current.fromX, tween.current.toX, easedProgress)
    playerRef.current.position.z = THREE.MathUtils.lerp(tween.current.fromZ, tween.current.toZ, easedProgress)
  })

  return <group ref={playerRef} position={initialPosition.current}>
    {player.team === 0
      ? <Rocket position={[0, 0, -0.1]} rotation={[0, -Math.PI * 1/16, 0]} scale={2} />
      : <UfoNew position={[0, 0, 0.2]} rotation={[0, 0, 0]} scale={2.0} animate={false} />}
    {isCurrentPlayer && <pointLight color='white' intensity={0.7} distance={2} />}
    <Label color='white' size={0.4} position={[-0.35, 0.05, 1.2]}>{player.name}</Label>
  </group>
}

function ResultTeamColumn({ team, teamName, players, winnerTeam, rowCount, labels, language }) {
  const isRocketTeam = team === 0
  const teamColor = isRocketTeam ? 'red' : 'turquoise'
  const isWinner = winnerTeam === team
  const columnX = isRocketTeam ? -2.35 : 2.35

  return <group position={[columnX, 0, -2.45]}>
    <Center position={[0, 0.25, 0.75]}><Text3D font={getNameFont(teamName, font)} size={0.42} height={0.01} rotation={[-Math.PI / 2, 0, 0]}><meshStandardMaterial color={teamColor} />{teamName}</Text3D></Center>
    {isRocketTeam
      ? <Rocket position={[0, 0, -0.25]} rotation={[0, -Math.PI / 16, 0]} scale={1.35} />
      : <UfoNew position={[0, 0, 0]} scale={1.3} animate={false} />}
    {players.map((player, index) => <Center key={player.id} position={[0, 0.25, 1.35 + index * 0.72]}>
      <Text3D font={getNameFont(player.name, font)} size={0.42} height={0.01} rotation={[-Math.PI / 2, 0, 0]}>
        <meshStandardMaterial color={teamColor} />
        {player.name}
      </Text3D>
    </Center>)}
    {Array.from({ length: rowCount - players.length }, (_, index) => <Center
      key={`extra-life-${index}`}
      position={[0, 0.25, 1.35 + (players.length + index) * 0.72]}
    >
      <Text3D font={font} size={0.42} height={0.01} rotation={[-Math.PI / 2, 0, 0]}>
        <meshStandardMaterial color={teamColor} />
        +1
      </Text3D>
    </Center>)}
    {isWinner && <YutBonus
      position={[0, 0.18, 2.55 + rowCount * 0.72]}
      scale={0.75}
      alwaysShow
      enableClick={false}
      modelOnly
    />}
    {isWinner && <Center position={[0.3, 0.25, 3.8 + rowCount * 0.72]}>
      <Text3D font={getNameFont(labels.freeYut[language], font)} size={0.3} height={0.01} rotation={[-Math.PI / 2, 0, 0]}>
        <meshStandardMaterial color='yellow' />
        {labels.freeYut[language]}
      </Text3D>
    </Center>}
  </group>
}

export default function DodgeMeteorsScene() {
  const client = useAtomValue(clientAtom)
  const language = useAtomValue(languageAtom)
  const teams = useAtomValue(teamsAtom)
  const state = useAtomValue(minigameStateAtom)
  const frame = useAtomValue(minigameDodgeFrameAtom)
  const [RollStar] = useStarRoll()
  const { id } = useParams()
  const roomId = id?.toUpperCase()
  const swipeStart = useRef(null)
  const handledCollisionIds = useRef(new Set())
  const handledGreenTransferIds = useRef(new Set())
  const [elapsedSeconds, setElapsedSeconds] = useState(0)
  const [continueSeconds, setContinueSeconds] = useState(10)
  const [greenTransfers, setGreenTransfers] = useState([])
  const phase = state.phase ?? 'start'
  const players = Object.values(Object.keys(frame.players ?? {}).length > 0 ? frame.players : state.players ?? {})
  const projectiles = frame.projectiles ?? []
  const playable = phase === 'demo' || phase === 'playing'
  const resultPlayers = Object.values(state.players ?? {})
  const teamPlayers = [0, 1].map((team) => resultPlayers.filter((player) => player.team === team))
  const resultRowCount = Math.max(...teamPlayers.map((playersForTeam) => playersForTeam.length))
  const winnerTeam = Number.isInteger(state.winnerTeam) ? state.winnerTeam : null
  const text = translations.minigames.starFall
  const labels = translations.minigames.labels
  const resultMessage = winnerTeam === null ? state.message : `${teams[winnerTeam]?.name || (winnerTeam === 0 ? labels.rockets[language] : labels.ufos[language])} ${labels.survived[language]}`
  const resultTitleColor = winnerTeam === 0 ? 'red' : winnerTeam === 1 ? 'turquoise' : 'yellow'
  const disadvantagedTeamLives = Math.max(...(frame.teamLives ?? [0, 0]))
  const teamPlayerCounts = [0, 1].map((team) => players.filter((player) => player.team === team).length)
  const disadvantagedTeam = teamPlayerCounts[0] === teamPlayerCounts[1]
    ? null
    : teamPlayerCounts[0] < teamPlayerCounts[1] ? 0 : 1
  const controlsPosition = [
    ARENA_WIDTH / 2,
    0,
    ((state.board?.maxZ ?? 6) / (state.board?.rowSubdivisions ?? 1)) * CELL_HEIGHT + ARENA_Z_OFFSET + 4
  ]

  function emit(event, payload = {}) {
    if (roomId) socket.emit(event, { roomId, ...payload })
  }

  function move(direction) {
    if (playable) emit('minigameDodgeInput', { direction })
  }
  useEffect(() => {
    if (phase !== 'playing') {
      setElapsedSeconds(0)
      return undefined
    }
    const startedAt = Date.now()
    const intervalId = window.setInterval(() => {
      setElapsedSeconds(Math.floor((Date.now() - startedAt) / 1000))
    }, 250)
    return () => window.clearInterval(intervalId)
  }, [phase])

  useEffect(() => {
    if (phase !== 'finished') {
      setContinueSeconds(10)
      return undefined
    }
    if (continueSeconds === 0) {
      emit('minigameReturnToGame')
      return undefined
    }
    const timeoutId = window.setTimeout(() => setContinueSeconds((seconds) => seconds - 1), 1000)
    return () => window.clearTimeout(timeoutId)
  }, [continueSeconds, phase])

  function playCollisionEffect(collision) {
    if (!collision?.id || handledCollisionIds.current.has(collision.id)) return
    handledCollisionIds.current.add(collision.id)
    const [x, z] = gridPosition(collision, state.board)
    const color = new THREE.Color(collision.team === 0 ? 'red' : 'turquoise')

    for (let burst = 0; burst < 10; burst += 1) {
      const angle = (burst / 10) * Math.PI * 2
      RollStar({
        position: new THREE.Vector3(x, 0.35, z),
        rotation: new THREE.Vector3(0, angle, 0),
        omitFactor: 6,
        size: 0.006,
        scale: 0.5,
        color,
        duration: 0.8,
        speedX: Math.cos(angle) * 3.2,
        speedY: Math.sin(angle) * 3.2,
        brightness: 0.1
      })
    }
  }

  useEffect(() => {
    const handleCollision = (collision) => playCollisionEffect(collision)
    socket.on('minigameDodgeCollision', handleCollision)
    return () => socket.off('minigameDodgeCollision', handleCollision)
  }, [RollStar, state.board])

  useEffect(() => {
    const handleGreenTransfer = (transfer) => {
      if (!transfer?.id || !transfer.from || !transfer.to || handledGreenTransferIds.current.has(transfer.id)) return
      handledGreenTransferIds.current.add(transfer.id)
      setGreenTransfers((current) => [...current, transfer])
    }
    socket.on('minigameDodgeGreenTransfer', handleGreenTransfer)
    return () => socket.off('minigameDodgeGreenTransfer', handleGreenTransfer)
  }, [])

  useEffect(() => {
    for (const collision of frame.collisions ?? []) playCollisionEffect(collision)
  }, [frame.collisions])

  useEffect(() => {
    const keyDirections = {
      ArrowLeft: 'left', a: 'left', A: 'left',
      ArrowRight: 'right', d: 'right', D: 'right',
      ArrowUp: 'up', w: 'up', W: 'up',
      ArrowDown: 'down', s: 'down', S: 'down'
    }
    function onKeyDown(event) {
      const direction = keyDirections[event.key]
      if (!direction || event.repeat) return
      event.preventDefault()
      move(direction)
    }
    window.addEventListener('keydown', onKeyDown)
    return () => {
      window.removeEventListener('keydown', onKeyDown)
      document.body.style.cursor = 'default'
    }
  }, [playable, roomId])

  function handleSwipeStart(event) {
    swipeStart.current = [event.point.x, event.point.z]
  }

  function handleSwipeEnd(event) {
    if (!swipeStart.current || !playable) return
    const deltaX = event.point.x - swipeStart.current[0]
    const deltaZ = event.point.z - swipeStart.current[1]
    swipeStart.current = null
    if (Math.max(Math.abs(deltaX), Math.abs(deltaZ)) < 0.25) return
    const direction = Math.abs(deltaX) > Math.abs(deltaZ)
      ? deltaX > 0 ? 'right' : 'left'
      : deltaZ < 0 ? 'up' : 'down'
    move(direction)
  }

  return <group name='star-fall-minigame'>
    {phase !== 'finished' && phase !== 'pending' && <>
      {phase === 'playing' && <Label color='yellow' size={0.52} position={[-1.1, 0.25, -7.2]}>{elapsedSeconds}</Label>}
      <StarfallBorder board={state.board} side={-1.3} />
      <StarfallBorder board={state.board} side={1.3} />
      {projectiles.map((projectile) => <FallingStar key={projectile.id} projectile={projectile} board={state.board} stepInterval={frame.meteorStepInterval} onTrailBurst={RollStar} />)}
      {greenTransfers.map((transfer) => <GreenStarTransfer key={transfer.id} transfer={transfer} board={state.board} onComplete={(id) => setGreenTransfers((current) => current.filter((item) => item.id !== id))} />)}
      {players.filter((player) => !player.eliminated && (!player.destroyedUntil || player.destroyedUntil <= Date.now())).map((player) => <DodgePlayer key={player.id} player={player} board={state.board} isCurrentPlayer={player.id === client?._id} />)}
      {phase === 'demo' && <>
        <Center position={[0, 0.25, -9.2]}><Label color='yellow' size={0.7} position={[0, 0, 0]}>{text.title[language]}</Label></Center>
        <Center position={[0, 0.25, -8]}><Label color='yellow' size={0.3} position={[0, 0, 0]}>{labels.demo[language]}</Label></Center>        <ActionButton position={[-3.7, 0, 8.2]} onClick={() => { emit('minigameStartGame'); trackEvent({ surface: 'minigame', action: 'minigame_started', category: 'gameplay', metadata: { type: 'dodge_meteors' } }) }}>{translations.minigames.actions.start[language]}</ActionButton>
      </>}
      {phase === 'countdown' && <Label color='yellow' size={1.3} position={[-0.42, 0.25, 0]}>{`${state.countdown}...`}</Label>}
      {playable && <group position={controlsPosition}>
        <ControlButton direction='up' position={[0, 0, -1.8]} onClick={() => move('up')} />
        <ControlButton direction='left' position={[-1.8, 0, 0]} onClick={() => move('left')} />
        <ControlButton direction='right' position={[1.8, 0, 0]} onClick={() => move('right')} />
        <ControlButton direction='down' position={[0, 0, 1.8]} onClick={() => move('down')} />
        {phase === 'playing' && disadvantagedTeam !== null && disadvantagedTeamLives > 0 && <group position={[-3, 0.22, 0]}>
          {disadvantagedTeam === 0
            ? <Rocket position={[-0.7, 0, -0.05]} rotation={[0, -Math.PI / 16, 0]} scale={1} />
            : <UfoNew position={[-0.52, 0, 0]} scale={1} animate={false} />}
          <Label color={disadvantagedTeam === 0 ? 'red' : 'turquoise'} size={0.48} position={[-0.1, 0.02, 0]}>{`+${disadvantagedTeamLives}`}</Label>
        </group>}
      </group>}
      {playable && <ActionButton position={[-4, 0, 10]} onClick={() => { emit('minigameReturnToGame'); trackEvent({ surface: 'minigame', action: 'exit', category: 'ui', metadata: { type: 'dodge_meteors' } }) }}>{translations.minigames.actions.back[language]}</ActionButton>}
    </>}
    {phase === 'finished' && <>
      <Center position={[0, 0.25, -5.4]}>
        <Text3D font={getNameFont(resultMessage || labels.timeUp[language], font)} size={0.8} height={0.01} rotation={[-Math.PI / 2, 0, 0]}>
          <meshStandardMaterial color={resultTitleColor} />
          {resultMessage || labels.timeUp[language]}
        </Text3D>
      </Center>
      {winnerTeam !== null && <Center position={[0, 0.25, -4.15]}>
        <Text3D font={getNameFont(labels.survived[language], font)} size={0.4} height={0.01} rotation={[-Math.PI / 2, 0, 0]}>
          <meshStandardMaterial color='white' />
          {`${labels.survived[language]} ${state.survivalSeconds ?? 0}`}
        </Text3D>
      </Center>}
      <ResultTeamColumn team={0} teamName={teams[0]?.name || labels.rockets[language]} players={teamPlayers[0]} winnerTeam={winnerTeam} rowCount={resultRowCount} labels={labels} language={language} />
      <ResultTeamColumn team={1} teamName={teams[1]?.name || labels.ufos[language]} players={teamPlayers[1]} winnerTeam={winnerTeam} rowCount={resultRowCount} labels={labels} language={language} />
      <ActionButton position={[0, 0, 4.9]} onClick={() => emit('minigameReturnToGame')}>{`${translations.minigames.actions.continue[language]} ${continueSeconds}`}</ActionButton>
    </>}
  </group>
}
